import React, { useState, useEffect } from 'react'

// Types
interface SalesforceField {
  id: string
  fieldName: string
  fieldLabel: string
  fieldType: string
  isRequired: boolean
  isUpdateable: boolean
}

interface FormField {
  id: string
  fieldName: string
  fieldLabel: string
  fieldType: string
  isRequired: boolean
  isActive: boolean
  isSystemField: boolean
  displayOrder: number
  placeholder?: string
  helpText?: string
}

interface FieldMapping {
  id: string
  formFieldId: string
  // Updated to match new server structure
  salesforceObjectName: string
  salesforceFieldName: string
  salesforceFieldLabel?: string
  formField?: FormField
  sfField?: SalesforceField
  transformationRules?: Record<string, any>
  isActive: boolean
}

const IsoConfigManager: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [connectionStatus, setConnectionStatus] = useState<{
    connected: boolean
    message: string
  } | null>(null)

  // Data states
  const [salesforceFields, setSalesforceFields] = useState<SalesforceField[]>([])
  const [formFields, setFormFields] = useState<FormField[]>([])
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([])

  // Modal states
  const [showAddFieldModal, setShowAddFieldModal] = useState(false)
  
  // Mapping states for inline editing
  const [editingMappings, setEditingMappings] = useState<{ [fieldId: string]: string }>({}) // fieldId -> selectedSalesforceFieldId

  // Form states
  const [newField, setNewField] = useState({
    field_label: '',
    field_type: 'text',
    is_required: false,
    placeholder: '',
    help_text: ''
  })



  // Load data on component mount
  useEffect(() => {
    testConnection()
    loadFormFields()
    loadFieldMappings()
    loadSalesforceFields() // Always load Salesforce fields
  }, [])

  const testConnection = async () => {
    try {
      setLoading(true)
      const response = await fetch('/api/admin/iso-config/test-connection', {
        credentials: 'include'
      })
      
      const data = await response.json()
      setConnectionStatus({
        connected: data.success,
        message: data.message
      })
    } catch (error) {
      setConnectionStatus({
        connected: false,
        message: 'Failed to test connection'
      })
    } finally {
      setLoading(false)
    }
  }

  const syncSalesforceFields = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await fetch('/api/admin/iso-config/sf-fields/sync', {
        method: 'POST',
        credentials: 'include'
      })
      
      if (response.ok) {
        const data = await response.json()
        alert(`Successfully synced ${data.data.upserted} fields from Salesforce!`)
        loadSalesforceFields() // Refresh Salesforce fields
      } else {
        const errorData = await response.json()
        setError(errorData.message || 'Failed to sync Salesforce fields')
      }
    } catch (error) {
      setError('Unable to sync fields. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Load Salesforce fields
  const loadSalesforceFields = async () => {
    try {
      const response = await fetch('/api/admin/iso-config/sf-fields', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        // The API returns { data: { fields, stats } }, so we need data.data.fields
        const fields = data.data?.fields || data.data || []
        setSalesforceFields(Array.isArray(fields) ? fields : [])
      } else {
        console.error('Failed to load Salesforce fields:', response.status, response.statusText)
        setSalesforceFields([])
      }
    } catch (error) {
      console.error('Failed to load Salesforce fields:', error)
      setSalesforceFields([])
    }
  }

  // Load form fields
  const loadFormFields = async () => {
    try {
      const response = await fetch('/api/admin/iso-config/form-fields', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        setFormFields(Array.isArray(data.data) ? data.data : [])
      } else {
        console.error('Failed to load form fields:', response.status, response.statusText)
        setFormFields([])
      }
    } catch (error) {
      console.error('Failed to load form fields:', error)
      setFormFields([])
    }
  }

  // Load field mappings
  const loadFieldMappings = async () => {
    try {
      const response = await fetch('/api/admin/iso-config/mappings', {
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        // The API returns { data: { mappings, stats, unmappedFields } }
        const mappings = data.data?.mappings || data.data || []

        setFieldMappings(Array.isArray(mappings) ? mappings : [])
      } else {
        console.error('Failed to load field mappings:', response.status, response.statusText)
        setFieldMappings([])
      }
    } catch (error) {
      console.error('Failed to load field mappings:', error)
      setFieldMappings([])
    }
  }

  // Create form field with optional mapping
  const createFormField = async () => {
    try {
      setLoading(true)
      setError(null)

      // Generate field name from label
      const fieldName = generateFieldName(newField.field_label)
      
      // Create the form field
      const fieldResponse = await fetch('/api/admin/iso-config/form-fields', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          fieldName: fieldName,
          fieldLabel: newField.field_label,
          fieldType: newField.field_type,
          isRequired: newField.is_required,
          placeholder: newField.placeholder,
          helpText: newField.help_text,
          displayOrder: formFields.length + 1, // Put new field at the end
          validationRules: null // No validation rules for now
        })
      })

      if (!fieldResponse.ok) {
        const errorData = await fieldResponse.json()
        console.error('Form field creation failed:', errorData)
        throw new Error(errorData.message || `Failed to create form field (${fieldResponse.status})`)
      }

      // Reset form and close modal
      setNewField({
        field_label: '',
        field_type: 'text',
        is_required: false,
        placeholder: '',
        help_text: ''
      })
      setShowAddFieldModal(false)

      // Refresh data
      loadFormFields()
      loadFieldMappings()
      
      alert('Field created successfully! You can now map it to a Salesforce field using the table below.')
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Generate field name from label
  const generateFieldName = (label: string): string => {
    return label.toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '')
  }

  // Check if types are compatible
  const areTypesCompatible = (formFieldType: string, salesforceType: string): boolean => {
    // The backend already maps Salesforce types to our internal types:
    // SF 'string' -> 'text', SF 'picklist' -> 'select', etc.
    // So we just need to check if they're the same or compatible
    const typeMap: { [key: string]: string[] } = {
      'text': ['text', 'email', 'phone', 'url'],
      'number': ['number'],
      'date': ['date', 'datetime', 'time'],
      'boolean': ['boolean'],
      'select': ['select', 'multiselect'],
      'textarea': ['textarea', 'text'],
      'multiselect': ['select', 'multiselect']
    }
    
    return typeMap[formFieldType]?.includes(salesforceType) || formFieldType === salesforceType
  }

  // Get selected form field for mapping validation
  const getSelectedFormField = (fieldId: string): FormField | undefined => {
    return formFields.find(f => f.id === fieldId)
  }

  // Get selected Salesforce field for mapping validation  
  const getSelectedSalesforceField = (fieldId: string): SalesforceField | undefined => {
    return salesforceFields.find(f => f.id === fieldId)
  }

  // Get current mapping for a form field
  const getCurrentMapping = (formFieldId: string): FieldMapping | undefined => {
    return fieldMappings.find(mapping => mapping.formFieldId === formFieldId)
  }

  // Save mapping for a form field
  const saveMapping = async (formFieldId: string, salesforceFieldId: string) => {
    try {
      setLoading(true)
      setError(null)

      // Find the Salesforce field details from salesforceFields
      const selectedSfField = salesforceFields.find(f => f.id === salesforceFieldId)
      if (!selectedSfField) {
        throw new Error('Selected Salesforce field not found')
      }

      // Check if mapping already exists
      const existingMapping = getCurrentMapping(formFieldId)

      if (existingMapping) {
        // Update existing mapping
        const response = await fetch(`/api/admin/iso-config/mappings/${existingMapping.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            salesforceObjectName: 'Account',
            salesforceFieldName: selectedSfField.fieldName
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to update mapping')
        }
      } else {
        // Create new mapping
        const response = await fetch('/api/admin/iso-config/mappings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            formFieldId: formFieldId,
            salesforceObjectName: 'Account',
            salesforceFieldName: selectedSfField.fieldName
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to create mapping')
        }
      }

      // Clear editing state
      setEditingMappings(prev => {
        const next = { ...prev }
        delete next[formFieldId]
        return next
      })

      // Refresh mappings after a short delay to ensure API has processed
      setTimeout(() => {
        loadFieldMappings()
      }, 100)
      
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Delete mapping for a form field
  const deleteMapping = async (formFieldId: string) => {
    try {
      setLoading(true)
      setError(null)

      const existingMapping = getCurrentMapping(formFieldId)
      if (!existingMapping) return

      const response = await fetch(`/api/admin/iso-config/mappings/${existingMapping.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete mapping')
      }

      // Refresh mappings
      loadFieldMappings()
      
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  // Delete form field (and its mappings via cascade)
  const deleteFormField = async (fieldId: string, fieldLabel: string) => {
    if (!confirm(`Are you sure you want to delete the field "${fieldLabel}"?\n\nThis will also remove any Salesforce mappings for this field and cannot be undone.`)) {
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await fetch(`/api/admin/iso-config/form-fields/${fieldId}?hard=true`, {
        method: 'DELETE',
        credentials: 'include'
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to delete form field')
      }

      // Refresh all data
      loadFormFields()
      loadFieldMappings()
      
    } catch (error: any) {
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="admin-content-header">
        <h2 className="admin-page-title">ISO Account Configuration</h2>
        <button 
          className="btn btn-primary"
          onClick={() => setShowAddFieldModal(true)}
        >
          <i className="bi bi-plus-lg me-1"></i>
          Add Field
        </button>
      </div>

      {/* Page Description */}
      <div className="px-4 py-3 bg-light border-bottom">
        <p className="mb-0 text-muted">
          Configure custom form fields for ISO signup and map them to Salesforce Account fields. 
          This allows you to collect specific data from ISOs and automatically sync it to your Salesforce organization.
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger alert-dismissible mx-4 mt-3" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Form Fields and Mappings */}
      <div className="card mx-4 mt-3">
        <div className="card-header">
          <h5 className="mb-0">
            <i className="bi bi-input-cursor me-2"></i>
            Custom Form Fields & Salesforce Mapping
          </h5>
        </div>
        <div className="card-body">
          {!Array.isArray(formFields) || formFields.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-input-cursor text-muted" style={{ fontSize: '3rem' }}></i>
              <h4 className="text-muted mt-3">No Form Fields</h4>
              <p className="text-muted">
                Create custom form fields for ISO signup forms and map them to Salesforce.
              </p>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddFieldModal(true)}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Add Your First Field
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Business Field Name</th>
                    <th>Field Type</th>
                    <th>Required</th>
                    <th>Salesforce Object</th>
                    <th>Salesforce Field</th>
                    <th style={{ minWidth: '220px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(formFields) && formFields.map((field) => {
                    const currentMapping = getCurrentMapping(field.id)
                    const isEditing = editingMappings[field.id] !== undefined
                    
                    return (
                      <tr key={field.id}>
                        <td>
                          <strong>{field.fieldLabel}</strong>
                          <div className="small text-muted">{field.fieldName}</div>
                          {field.isSystemField && (
                            <div className="mt-1">
                              <span className="badge bg-info text-white">
                                <i className="bi bi-shield-check me-1"></i>
                                System Field
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-secondary">{field.fieldType}</span>
                        </td>
                        <td>
                          {field.isRequired ? (
                            <span className="badge bg-warning text-dark">Required</span>
                          ) : (
                            <span className="badge bg-light text-dark">Optional</span>
                          )}
                        </td>
                        <td>
                          <span className="badge bg-primary">Account</span>
                        </td>
                        <td>
                          {isEditing ? (
                            <div className="d-flex gap-2">
                              <select
                                className="form-select form-select-sm"
                                value={editingMappings[field.id] || ''}
                                onChange={(e) => setEditingMappings(prev => ({
                                  ...prev,
                                  [field.id]: e.target.value
                                }))}
                              >
                                <option value="">Select Salesforce field...</option>
                                {Array.isArray(salesforceFields) && salesforceFields
                                  .map(sf => (
                                    <option key={sf.id} value={sf.id}>
                                      {sf.fieldLabel} ({sf.fieldType})
                                    </option>
                                  ))}
                              </select>
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => saveMapping(field.id, editingMappings[field.id])}
                                disabled={!editingMappings[field.id] || loading}
                                title="Save mapping"
                              >
                                <i className="bi bi-check me-1"></i>
                                Save
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => setEditingMappings(prev => {
                                  const next = { ...prev }
                                  delete next[field.id]
                                  return next
                                })}
                                title="Cancel editing"
                              >
                                <i className="bi bi-x me-1"></i>
                                Cancel
                              </button>
                            </div>
                          ) : currentMapping ? (
                            <div>
                              <strong>{currentMapping.salesforceFieldLabel || currentMapping.salesforceFieldName}</strong>
                              <div className="small text-muted">{currentMapping.salesforceFieldName}</div>
                              <div className="mt-1">
                                {currentMapping.sfField && areTypesCompatible(field.fieldType, currentMapping.sfField.fieldType) ? (
                                  <span className="badge bg-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Compatible
                                  </span>
                                ) : (
                                  <span className="badge bg-warning">
                                    <i className="bi bi-info-circle me-1"></i>
                                    Needs Validation
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted">Not mapped</span>
                          )}
                        </td>
                        <td style={{ minWidth: '220px' }}>
                          <div className="d-flex gap-2 flex-wrap">
                            {currentMapping && !isEditing && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => {
                                    // Find the salesforce field ID that matches the field name
                                    const sfField = salesforceFields.find(sf => sf.fieldName === currentMapping.salesforceFieldName)
                                    setEditingMappings(prev => ({
                                      ...prev,
                                      [field.id]: sfField?.id || ''
                                    }))
                                  }}
                                  title="Edit Salesforce mapping"
                                >
                                  <i className="bi bi-pencil me-1"></i>
                                  Edit Map
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-warning"
                                  onClick={() => deleteMapping(field.id)}
                                  title="Remove Salesforce mapping"
                                >
                                  <i className="bi bi-unlink me-1"></i>
                                  Unmap
                                </button>
                              </>
                            )}
                            {!currentMapping && !isEditing && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => setEditingMappings(prev => ({
                                  ...prev,
                                  [field.id]: ''
                                }))}
                                title="Map to Salesforce field"
                              >
                                <i className="bi bi-link-45deg me-1"></i>
                                Map to SF
                              </button>
                            )}
                            
                            {/* Field delete button - only show for non-system fields */}
                            {!field.isSystemField && (
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => deleteFormField(field.id, field.fieldLabel)}
                                title="Delete this form field permanently"
                                disabled={loading}
                              >
                                <i className="bi bi-trash3 me-1"></i>
                                Delete Field
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>



      {/* Add Field Modal */}
      {showAddFieldModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-plus-circle me-2"></i>
                  Add Custom Form Field
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddFieldModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); createFormField(); }}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Field Label *</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newField.field_label}
                          onChange={(e) => setNewField({
                            ...newField,
                            field_label: e.target.value
                          })}
                          placeholder="e.g., Business Legal Name"
                          required
                        />
                        <div className="form-text">This will be displayed to users</div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Field Type *</label>
                        <select
                          className="form-select"
                          value={newField.field_type}
                          onChange={(e) => setNewField({
                            ...newField,
                            field_type: e.target.value
                          })}
                          required
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="boolean">Boolean (Yes/No)</option>
                          <option value="select">Select (Dropdown)</option>
                          <option value="textarea">Textarea</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Placeholder Text</label>
                        <input
                          type="text"
                          className="form-control"
                          value={newField.placeholder}
                          onChange={(e) => setNewField({
                            ...newField,
                            placeholder: e.target.value
                          })}
                          placeholder="e.g., Enter your business name"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="isRequired"
                            checked={newField.is_required}
                            onChange={(e) => setNewField({
                              ...newField,
                              is_required: e.target.checked
                            })}
                          />
                          <label className="form-check-label" htmlFor="isRequired">
                            Required field
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Help Text</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={newField.help_text}
                      onChange={(e) => setNewField({
                        ...newField,
                        help_text: e.target.value
                      })}
                      placeholder="Optional help text to guide users"
                    />
                  </div>

                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Tip:</strong> After creating the field, you can map it to a Salesforce field using the table below.
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddFieldModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createFormField}
                  disabled={loading || !newField.field_label}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-lg me-1"></i>
                      Create Field
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  )
}

export default IsoConfigManager
